import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ImportEntity, ImportPlan, ImportRow, ImportVendor } from '../../models/import';
import { ImportService } from '../../../services/import.service';

/**
 * Loads a practice's data out of the software it is leaving, one file at a time.
 *
 * Always two steps. The preview writes nothing and reports what every row would do; only then is
 * Import offered, and it re-sends the same file with the checksum the preview returned so the
 * backend can refuse a file that is not the one reviewed.
 */
@Component({
  selector: 'app-tenant-import',
  templateUrl: './tenant-import.component.html',
  styleUrls: ['./tenant-import.component.scss']
})
export class TenantImportComponent implements OnInit {

  readonly vendors: { value: ImportVendor; label: string }[] = [
    { value: 'ALPINDENT', label: 'Alpindent' }
  ];

  readonly entities: { value: ImportEntity; label: string }[] = [
    { value: 'PRODUCTS', label: 'Products & categories' }
  ];

  tenantId!: string;
  vendor: ImportVendor = 'ALPINDENT';
  entity: ImportEntity = 'PRODUCTS';

  file: File | null = null;
  plan: ImportPlan | null = null;

  previewing = false;
  importing = false;
  error: string | null = null;
  success: string | null = null;

  constructor(private route: ActivatedRoute, private importService: ImportService) {}

  ngOnInit(): void {
    this.tenantId = this.route.snapshot.paramMap.get('id') ?? '';
  }

  onFileSelected(file: File): void {
    this.file = file;
    // Any previous plan described a different file; keeping it on screen would invite importing
    // something the admin never reviewed.
    this.reset();
  }

  onFileRemoved(): void {
    this.file = null;
    this.reset();
  }

  onSettingChanged(): void {
    this.reset();
  }

  preview(): void {
    if (!this.file || this.previewing) {
      return;
    }
    this.previewing = true;
    this.error = null;
    this.success = null;

    this.importService.preview(this.tenantId, this.file, this.vendor, this.entity).subscribe({
      next: (plan) => {
        this.plan = plan;
        this.previewing = false;
      },
      error: (err) => {
        this.error = this.messageOf(err, 'Could not read the file.');
        this.previewing = false;
      }
    });
  }

  runImport(): void {
    if (!this.file || !this.plan || this.importing || this.blocked) {
      return;
    }
    this.importing = true;
    this.error = null;

    this.importService.run(this.tenantId, this.file, this.vendor, this.entity, this.plan.checksum).subscribe({
      next: (plan) => {
        this.plan = plan;
        this.importing = false;
        this.success = `Imported ${plan.totals.created} new and ${plan.totals.updated} updated `
          + `product(s); ${plan.totals.unchanged} already matched.`;
      },
      error: (err) => {
        this.error = this.messageOf(err, 'The import failed.');
        this.importing = false;
      }
    });
  }

  /** Whether the file has anything that stops it being imported. */
  get blocked(): boolean {
    return !!this.plan && this.plan.totals.errors > 0;
  }

  get committed(): boolean {
    return !!this.plan && this.plan.committed;
  }

  get errorRows(): ImportRow[] {
    return this.plan ? this.plan.rows.filter(row => row.action === 'ERROR') : [];
  }

  /** Errors first — they are what the admin has to act on — then the file's own order. */
  get sortedRows(): ImportRow[] {
    if (!this.plan) {
      return [];
    }
    return [...this.plan.rows].sort((a, b) => {
      if ((a.action === 'ERROR') !== (b.action === 'ERROR')) {
        return a.action === 'ERROR' ? -1 : 1;
      }
      return a.line - b.line;
    });
  }

  badgeClass(action: string): string {
    return `badge badge-${action.toLowerCase()}`;
  }

  trackByLine(_index: number, row: ImportRow): number {
    return row.line;
  }

  private reset(): void {
    this.plan = null;
    this.error = null;
    this.success = null;
  }

  private messageOf(err: any, fallback: string): string {
    return err?.error?.exceptionMessage || err?.error?.message || fallback;
  }
}
